import type { Idl, Program } from '@coral-xyz/anchor';
import {
  type AccountInfo,
  type Connection,
  type PublicKey,
  SYSVAR_CLOCK_PUBKEY,
} from '@solana/web3.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveBondingCurvePda, deriveOutcomePda } from '../pda/derivations.js';
import { decodeChainClock } from './chain-clock.js';
import { fetchGraduatedPoolInfoByPool } from './fetch.js';
import type {
  AnchorEnum,
  BackerFeeShareConfigAccount,
  BondingCurveAccount,
  MarketAccount,
  MarketConfigAccount,
  OutcomeAccount,
} from './types.js';

export type ChainMarketLifecycle = 'created' | 'backing' | 'trading' | 'resolved' | 'graduated';
export type ChainCreatorFeeOn = 'both' | 'token0' | 'token1';

export interface BondingCurvePoolSnapshot {
  virtualQuoteReserveD6: string;
  poolQuoteReserveD6: string;
  initialOutcomeSupplyD6: string;
  poolOutcomeReserveD6: string;
  /** Market-tracked sold supply while BC trading is active; reset during graduation. */
  trackedBondingCurveSoldSupplyD6: string;
  closedForTrade: boolean;
}

export interface BondingCurveTradeGateSnapshot {
  outcomeConvictionPpb: string[];
  lastConvictionUpdateUnixSeconds: string;
  convictionBaselineReserveQuoteD6: string;
  convictionRetentionPerSecondPpb: string;
  convictionResolutionThresholdPpb: string;
  /** Present on fresh snapshots; optional only for persisted pre-upgrade cache compatibility. */
  convictionReferenceShareBps?: number;
}

export interface CpSwapPoolSnapshot {
  pool: string;
  quoteReserveD6: string;
  outcomeReserveD6: string;
  tradeFeeRateMillionths: string;
  creatorFeeRateMillionths: string;
  creatorFeeEnabled: boolean;
  creatorFeeOn: ChainCreatorFeeOn;
  outcomeTokenIndex: 0 | 1;
  swapEnabled: boolean;
  openTimeUnixSeconds: string;
}

export interface OutcomePoolSnapshot {
  outcomeIndex: number;
  outcomeMint: string;
  /** Only present while the market-backed bonding curve state still exists. */
  realQuoteReserveD6: string | null;
  /** Null for migrated winners and terminal losers after BC cleanup. */
  bondingCurve: BondingCurvePoolSnapshot | null;
  cpSwap: CpSwapPoolSnapshot | null;
}

export interface MarketPoolSnapshot {
  market: string;
  /** Solana Clock observed atomically with the mutable pool state. */
  chainSlot: string;
  chainUnixTimestampSeconds: string;
  lifecycle: ChainMarketLifecycle;
  winningOutcomeIndex: number | null;
  /** Null when the persistent Outcome path no longer depends on the Market PDA. */
  bondingCurvesClosed: boolean | null;
  penaltyTargetOutcomeIndex: number | null;
  backerFeeOmissionBps?: number;
  buyFeeBps: number | null;
  sellBaseFeeBps: number | null;
  sellPenaltyFeeBps: number | null;
  prizePoolFeeBps: number | null;
  minimumQuoteTradeD6: string | null;
  bondingCurveTradeGate: BondingCurveTradeGateSnapshot | null;
  outcomes: OutcomePoolSnapshot[];
}

/**
 * Fetch the complete executable quote state for every outcome in one market.
 * Amounts are returned as base-10 strings so callers may safely persist the
 * snapshot in browser or native query caches before hydrating it to bigint.
 */
export async function fetchMarketPoolSnapshot(
  connection: Connection,
  marketAddress: PublicKey,
  expectedQuoteMint: PublicKey,
  expectedOutcomeCount?: number,
  minimumContextSlot?: number,
): Promise<MarketPoolSnapshot> {
  if (expectedOutcomeCount !== undefined) requireExpectedOutcomeCount(expectedOutcomeCount);
  requireMinimumContextSlot(minimumContextSlot);
  const program = await getProgram(connection, MORFI_OPINION_PROGRAM_ID);
  const persistentOutcomes = await fetchPersistentOutcomes(
    connection,
    program,
    marketAddress,
    expectedOutcomeCount,
    minimumContextSlot,
  );
  const migratedOutcomes = persistentOutcomes.filter(({ account }) => account.cpSwapPool !== null);
  if (migratedOutcomes.length > 1) {
    throw new Error('Market has more than one Outcome with a recorded cp-swap pool');
  }
  const migratedWinner = migratedOutcomes[0];
  if (migratedWinner !== undefined) {
    return fetchMigratedMarketSnapshot(
      connection,
      marketAddress,
      persistentOutcomes,
      migratedWinner,
      expectedQuoteMint,
      minimumContextSlot,
    );
  }

  const discoveredMarketResult = await connection.getAccountInfoAndContext(
    marketAddress,
    minimumContextConfig(minimumContextSlot),
  );
  const discoveredMarket = decodeProgramAccount<MarketAccount>(
    program,
    'market',
    marketAddress,
    accountInfoAt([discoveredMarketResult.value], 0, 'Market'),
  );
  if (discoveredMarket.nOutcomes !== persistentOutcomes.length) {
    throw new Error('Expected outcome count does not match the on-chain Market');
  }
  const curveAddresses = persistentOutcomes.map(({ account }) => account.bondingCurve);
  const batch = await connection.getMultipleAccountsInfoAndContext(
    [marketAddress, discoveredMarket.config, ...curveAddresses, SYSVAR_CLOCK_PUBKEY],
    minimumContextConfig(minimumContextSlot),
  );
  const market = decodeProgramAccount<MarketAccount>(
    program,
    'market',
    marketAddress,
    accountInfoAt(batch.value, 0, 'Market'),
  );
  if (
    !market.config.equals(discoveredMarket.config) ||
    market.nOutcomes !== discoveredMarket.nOutcomes
  ) {
    throw new Error('Market shape changed during snapshot discovery');
  }
  const lifecycle = marketLifecycle(market.state);
  if (lifecycle === 'graduated') {
    throw new Error('Graduated Market is missing the cp-swap pool recorded on its Outcome');
  }
  const marketConfig = decodeProgramAccount<MarketConfigAccount>(
    program,
    'marketConfig',
    market.config,
    accountInfoAt(batch.value, 1, 'MarketConfig'),
  );
  if (!marketConfig.quoteMint.equals(expectedQuoteMint)) {
    throw new Error('MarketConfig quote mint does not match the active-network USDC mint');
  }
  let backerFeeOmissionBps = 0;
  if (marketConfig.minPrizePoolQuote.isZero()) {
    const splitInfo = await connection.getAccountInfo(
      marketConfig.backerFeeShareConfig,
      minimumContextConfig(batch.context.slot),
    );
    if (splitInfo === null) throw new Error('Featured fee split account is missing');
    backerFeeOmissionBps = decodeProgramAccount<BackerFeeShareConfigAccount>(
      program,
      'backerFeeShareConfig',
      marketConfig.backerFeeShareConfig,
      splitInfo,
    ).feeSplitBackersBps;
  }
  const chainClock = decodeChainClock(
    accountInfoAt(batch.value, curveAddresses.length + 2, 'Solana Clock sysvar'),
  );
  const bondingCurves = curveAddresses.map((address, outcomeIndex) => {
    const curve = decodeProgramAccount<BondingCurveAccount>(
      program,
      'bondingCurve',
      address,
      accountInfoAt(batch.value, outcomeIndex + 2, `BondingCurve ${outcomeIndex}`),
    );
    validateBondingCurveBinding(curve, marketAddress, outcomeIndex, persistentOutcomes);
    return curve;
  });

  const outcomes = bondingCurves.map((curve, outcomeIndex): OutcomePoolSnapshot => {
    const outcomeMint = market.outcomeMints[outcomeIndex];
    if (outcomeMint === undefined) {
      throw new Error(`Market is missing outcome mint ${outcomeIndex}`);
    }
    const realQuoteReserve = market.quoteReserves[outcomeIndex];
    if (realQuoteReserve === undefined) {
      throw new Error(`Market is missing quote reserve ${outcomeIndex}`);
    }
    const persistentOutcome = persistentOutcomes[outcomeIndex]?.account;
    if (persistentOutcome === undefined || !persistentOutcome.outcomeMint.equals(outcomeMint)) {
      throw new Error(`Market outcome mint ${outcomeIndex} does not match its Outcome PDA`);
    }
    return {
      outcomeIndex,
      outcomeMint: outcomeMint.toBase58(),
      realQuoteReserveD6: realQuoteReserve.toString(),
      bondingCurve: {
        virtualQuoteReserveD6: curve.virtualQuoteReserves.toString(),
        poolQuoteReserveD6: curve.virtualQuoteReserves.add(realQuoteReserve).toString(),
        initialOutcomeSupplyD6: curve.initialTokenSupply.toString(),
        poolOutcomeReserveD6: curve.tokensInCurveVault.toString(),
        trackedBondingCurveSoldSupplyD6: curve.realTokenReserves.toString(),
        closedForTrade: curve.closedForTrade,
      },
      cpSwap: null,
    };
  });

  return {
    market: marketAddress.toBase58(),
    chainSlot: chainClock.slot.toString(),
    chainUnixTimestampSeconds: chainClock.unixTimestampSeconds.toString(),
    lifecycle,
    winningOutcomeIndex: market.winningOutcome,
    bondingCurvesClosed: market.bondingCurvesClosed,
    penaltyTargetOutcomeIndex:
      market.cachedPenaltyTargetIdx === 255 ? null : market.cachedPenaltyTargetIdx,
    backerFeeOmissionBps,
    buyFeeBps: marketConfig.feeBpsBuy,
    sellBaseFeeBps: marketConfig.feeBpsSellNormal,
    sellPenaltyFeeBps: marketConfig.feeBpsSellPenaltyExtra,
    prizePoolFeeBps: marketConfig.feeBpsPrizePool,
    minimumQuoteTradeD6: marketConfig.minQuoteInPerTrade.toString(),
    bondingCurveTradeGate: {
      outcomeConvictionPpb: market.outcomeConvictionPpb
        .slice(0, market.nOutcomes)
        .map((value) => value.toString()),
      lastConvictionUpdateUnixSeconds: market.lastConvictionUpdateTs.toString(),
      convictionBaselineReserveQuoteD6: marketConfig.convictionBaselineReserveQuote.toString(),
      convictionRetentionPerSecondPpb: marketConfig.convictionRetentionPerSecondPpb.toString(),
      convictionResolutionThresholdPpb: marketConfig.convictionResolutionThresholdPpb.toString(),
      convictionReferenceShareBps: marketConfig.convictionReferenceShareBps,
    },
    outcomes,
  };
}

interface PersistentOutcome {
  address: PublicKey;
  account: OutcomeAccount;
}

async function fetchPersistentOutcomes(
  connection: Connection,
  program: Program<Idl>,
  marketAddress: PublicKey,
  expectedOutcomeCount: number | undefined,
  minimumContextSlot: number | undefined,
): Promise<PersistentOutcome[]> {
  const addresses = Array.from({ length: 10 }, (_, outcomeIndex) => {
    const [address] = deriveOutcomePda(marketAddress, outcomeIndex);
    return address;
  });
  const batch = await connection.getMultipleAccountsInfoAndContext(
    addresses,
    minimumContextConfig(minimumContextSlot),
  );
  if (batch.value.length !== addresses.length) {
    throw new Error('Persistent Outcome snapshot returned an unexpected account count');
  }
  const firstMissing = batch.value.indexOf(null);
  const discoveredCount = firstMissing === -1 ? addresses.length : firstMissing;
  if (discoveredCount === 0) {
    throw new Error('Market has no persistent Outcome accounts');
  }
  if (batch.value.slice(discoveredCount).some((account) => account !== null)) {
    throw new Error('Market Outcome accounts do not form a contiguous prefix');
  }
  if (expectedOutcomeCount !== undefined && discoveredCount !== expectedOutcomeCount) {
    throw new Error('Expected outcome count does not match persistent Outcome accounts');
  }
  return addresses.slice(0, discoveredCount).map((address, outcomeIndex) => {
    const account = decodeProgramAccount<OutcomeAccount>(
      program,
      'outcome',
      address,
      accountInfoAt(batch.value, outcomeIndex, `Outcome ${outcomeIndex}`),
    );
    validatePersistentOutcome(account, marketAddress, outcomeIndex);
    return { address, account };
  });
}

async function fetchMigratedMarketSnapshot(
  connection: Connection,
  marketAddress: PublicKey,
  outcomes: PersistentOutcome[],
  winner: PersistentOutcome,
  expectedQuoteMint: PublicKey,
  minimumContextSlot: number | undefined,
): Promise<MarketPoolSnapshot> {
  const poolState = winner.account.cpSwapPool;
  if (poolState === null) {
    throw new Error('Migrated Outcome is missing its recorded cp-swap pool');
  }
  const info = await fetchGraduatedPoolInfoByPool(connection, {
    poolState,
    outcomeAddress: winner.address,
    outcome: winner.account,
    expectedQuoteMint,
    ...(minimumContextSlot === undefined ? {} : { minimumContextSlot }),
  });
  return {
    market: marketAddress.toBase58(),
    chainSlot: info.chainSlot.toString(),
    chainUnixTimestampSeconds: info.chainUnixTimestampSeconds.toString(),
    lifecycle: 'graduated',
    winningOutcomeIndex: winner.account.outcomeIndex,
    bondingCurvesClosed: null,
    penaltyTargetOutcomeIndex: null,
    buyFeeBps: null,
    sellBaseFeeBps: null,
    sellPenaltyFeeBps: null,
    prizePoolFeeBps: null,
    minimumQuoteTradeD6: null,
    bondingCurveTradeGate: null,
    outcomes: outcomes.map(
      ({ account }): OutcomePoolSnapshot => ({
        outcomeIndex: account.outcomeIndex,
        outcomeMint: account.outcomeMint.toBase58(),
        realQuoteReserveD6: null,
        bondingCurve: null,
        cpSwap: account.outcomeIndex === winner.account.outcomeIndex ? cpSwapSnapshot(info) : null,
      }),
    ),
  };
}

function requireMinimumContextSlot(minimumContextSlot: number | undefined): void {
  if (
    minimumContextSlot !== undefined &&
    (!Number.isSafeInteger(minimumContextSlot) || minimumContextSlot < 0)
  ) {
    throw new Error('minimum context slot must be a non-negative safe integer');
  }
}

function minimumContextConfig(
  minimumContextSlot: number | undefined,
): { readonly minContextSlot: number } | undefined {
  return minimumContextSlot === undefined ? undefined : { minContextSlot: minimumContextSlot };
}

function validatePersistentOutcome(
  outcome: OutcomeAccount,
  marketAddress: PublicKey,
  outcomeIndex: number,
): void {
  if (!outcome.market.equals(marketAddress) || outcome.outcomeIndex !== outcomeIndex) {
    throw new Error(`Outcome ${outcomeIndex} does not match its market/index binding`);
  }
  const [expectedBondingCurve] = deriveBondingCurvePda(marketAddress, outcomeIndex);
  if (!outcome.bondingCurve.equals(expectedBondingCurve)) {
    throw new Error(`Outcome ${outcomeIndex} records a non-canonical BondingCurve`);
  }
}

function validateBondingCurveBinding(
  curve: BondingCurveAccount,
  marketAddress: PublicKey,
  outcomeIndex: number,
  outcomes: PersistentOutcome[],
): void {
  const outcome = outcomes[outcomeIndex]?.account;
  if (
    outcome === undefined ||
    !curve.market.equals(marketAddress) ||
    curve.outcomeIndex !== outcomeIndex ||
    !curve.outcomeMint.equals(outcome.outcomeMint)
  ) {
    throw new Error(`BondingCurve ${outcomeIndex} does not match its Outcome PDA`);
  }
}

function requireExpectedOutcomeCount(value: number): void {
  if (!Number.isSafeInteger(value) || value < 1 || value > 10) {
    throw new Error('Expected outcome count must be an integer between 1 and 10');
  }
}

function accountInfoAt(
  accounts: ReadonlyArray<AccountInfo<Buffer> | null>,
  index: number,
  label: string,
): AccountInfo<Buffer> {
  const account = accounts[index];
  if (account === undefined || account === null) {
    throw new Error(`${label} is missing from atomic snapshot`);
  }
  return account;
}

function decodeProgramAccount<T>(
  program: Program<Idl>,
  accountName: string,
  address: PublicKey,
  info: AccountInfo<Buffer>,
): T {
  if (!info.owner.equals(program.programId)) {
    throw new Error(`${accountName} ${address.toBase58()} has an unexpected owner`);
  }
  return program.coder.accounts.decode<T>(accountName, info.data);
}

function cpSwapSnapshot(
  info: Awaited<ReturnType<typeof fetchGraduatedPoolInfoByPool>>,
): CpSwapPoolSnapshot {
  return {
    pool: info.poolState.toBase58(),
    quoteReserveD6: info.usdcReserves.toString(),
    outcomeReserveD6: info.outcomeReserves.toString(),
    tradeFeeRateMillionths: info.tradeFeeRateMillionths.toString(),
    creatorFeeRateMillionths: info.creatorFeeRateMillionths.toString(),
    creatorFeeEnabled: info.creatorFeeEnabled,
    creatorFeeOn: creatorFeeOn(info.creatorFeeOn),
    outcomeTokenIndex: info.quoteTokenIndex === 0 ? 1 : 0,
    swapEnabled: info.swapEnabled,
    openTimeUnixSeconds: info.openTime.toString(),
  };
}

function marketLifecycle(
  value: AnchorEnum<'created' | 'backing' | 'trading' | 'resolved' | 'graduated'>,
): ChainMarketLifecycle {
  const variants = ['created', 'backing', 'trading', 'resolved', 'graduated'] as const;
  for (const variant of variants) {
    if (value === variant || (typeof value === 'object' && variant in value)) return variant;
  }
  throw new Error('Unknown market lifecycle variant');
}

function creatorFeeOn(value: number): ChainCreatorFeeOn {
  if (value === 0) return 'both';
  if (value === 1) return 'token0';
  if (value === 2) return 'token1';
  throw new Error('Unknown cp-swap creator-fee mode');
}
